import { useEffect, useRef, useState } from "react";

const LOOP_URL = "/audio/trance-loop.wav";
let rawPromise = null;
const getRawAudio = () => {
    if (!rawPromise) rawPromise = fetch(LOOP_URL).then((r) => r.arrayBuffer());
    return rawPromise;
};

class AudioLoop {
    constructor(level) {
        this.level = level;
        this.ctx = null;
        this.src = null;
    }

    async start() {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        this.ctx = ctx;
        this.master = ctx.createGain();
        this.master.gain.value = 0;
        this.master.connect(ctx.destination);
        const raw = await getRawAudio();
        if (!this.ctx) return;
        const buf = await ctx.decodeAudioData(raw.slice(0));
        if (!this.ctx) return;
        const src = ctx.createBufferSource();
        src.buffer = buf;
        src.loop = true;
        src.connect(this.master);
        src.start();
        this.src = src;
        this.master.gain.setTargetAtTime(0.34 * this.level, ctx.currentTime, 1.4);
    }

    setVolume(v) {
        this.level = v;
        if (this.ctx) this.master.gain.setTargetAtTime(0.34 * v, this.ctx.currentTime, 0.1);
    }

    stop() {
        if (!this.ctx) return;
        const ctx = this.ctx;
        const src = this.src;
        this.ctx = null;
        this.src = null;
        this.master.gain.setTargetAtTime(0, ctx.currentTime, 0.5);
        setTimeout(() => {
            try { src?.stop(); } catch {}
            ctx.close();
        }, 1800);
    }
}

export const SoundToggle = () => {
    const [on, setOn] = useState(false);
    const [level, setLevel] = useState(0.8);
    const engine = useRef(null);

    useEffect(() => () => engine.current?.stop(), []);

    const toggle = () => {
        if (on) {
            engine.current?.stop();
            window.__mtaSound = null;
            setOn(false);
        } else {
            engine.current = new AudioLoop(level);
            engine.current.start();
            window.__mtaSound = engine.current;
            setOn(true);
        }
    };

    const onVol = (e) => {
        const v = parseFloat(e.target.value);
        setLevel(v);
        engine.current?.setVolume(v);
    };

    return (
        <div className="group flex items-center" data-testid="sound-control">
            <div
                className={`w-0 overflow-hidden opacity-0 transition-[width,opacity,margin] duration-500 ${
                    on ? "focus-within:mr-2 focus-within:w-24 focus-within:opacity-100 group-hover:mr-2 group-hover:w-24 group-hover:opacity-100" : ""
                }`}
            >
                <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={level}
                    onChange={onVol}
                    data-testid="sound-volume"
                    aria-label="Sound volume"
                    className="sound-range h-3 w-24 cursor-pointer"
                />
            </div>
            <button
                type="button"
                data-testid="sound-toggle"
                aria-pressed={on}
                onClick={toggle}
                className="flex items-center gap-2.5 border border-white/25 px-3 py-2 text-white/70 transition-colors duration-500 hover:border-white/50 hover:text-white"
            >
                <span className="flex h-3 items-end gap-[2px]">
                    {[0, 1, 2, 3].map((i) => (
                        <span
                            key={i}
                            className="w-[2px] bg-current"
                            style={{
                                height: "100%",
                                transformOrigin: "bottom",
                                transform: on ? undefined : "scaleY(0.25)",
                                animation: on ? `soundbar ${0.7 + i * 0.18}s ease-in-out ${-i * 0.15}s infinite` : "none",
                            }}
                        />
                    ))}
                </span>
                <span className="hidden font-mono-tech text-[9px] tracking-[0.25em] sm:inline">{on ? "SOUND ON" : "SOUND"}</span>
            </button>
        </div>
    );
};
